import { TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';

import { useTableFilters } from '../../foundation/hooks';
import {
  useGetSampleFilterOptionsQuery,
  useGetSamplesQuery,
} from '../api/variantApi';
import type { Sample } from './types';

export type ProbandsSamplesColumnFilters = {
  sample_id: string[];
  subject_external_id: string[];
  indication: string[];
  reporter_display_name: string[];
  linked_variants: string[];
  entry_date: string[];
};

const initialColumnFilters: ProbandsSamplesColumnFilters = {
  sample_id: [],
  subject_external_id: [],
  indication: [],
  reporter_display_name: [],
  linked_variants: [],
  entry_date: [],
};

const PAGE_SIZE = 20;

export const useProbandsSamplesTable = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>(
    undefined,
  );
  const {
    columnFilters: sharedColumnFilters,
    dateFrom: entryDateFrom,
    dateTo: entryDateTo,
    setDateFrom,
    setDateTo,
    handleFilterChange: changeFilter,
    handleFilterClear: clearFilter,
    handleClearAllFilters: clearAllFilters,
    getActiveFiltersCount,
    getDateBounds,
  } = useTableFilters({
    initialFilters: initialColumnFilters,
    dateFilterKey: 'entry_date',
  });
  const columnFilters = sharedColumnFilters as ProbandsSamplesColumnFilters;

  const { data: samplesData, isFetching: isSamplesLoading } =
    useGetSamplesQuery({
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
      search: appliedSearch.trim() || undefined,
      sortField,
      sortOrder,
      sample_id_filter:
        columnFilters.sample_id.length > 0
          ? columnFilters.sample_id
          : undefined,
      subject_filter:
        columnFilters.subject_external_id.length > 0
          ? columnFilters.subject_external_id
          : undefined,
      indication_filter:
        columnFilters.indication.length > 0
          ? columnFilters.indication
          : undefined,
      reporter_filter:
        columnFilters.reporter_display_name.length > 0
          ? columnFilters.reporter_display_name
          : undefined,
      linked_variants_filter:
        columnFilters.linked_variants.length > 0
          ? columnFilters.linked_variants
          : undefined,
      entry_date_filter:
        columnFilters.entry_date.length > 0
          ? columnFilters.entry_date
          : undefined,
      entry_date_from: entryDateFrom || undefined,
      entry_date_to: entryDateTo || undefined,
    });

  const { data: filterOptionsData } = useGetSampleFilterOptionsQuery();

  const entryDateBounds = getDateBounds(filterOptionsData?.entry_date);

  const samples = samplesData?.results ?? [];
  const totalSamples = samplesData?.total_result_count ?? 0;

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedSearch(searchInput);
  };

  const handleFilterChange = useCallback(
    (columnKey: string, values: string[]) => {
      changeFilter(columnKey, values);
      setCurrentPage(1);
    },
    [changeFilter],
  );

  const handleFilterClear = useCallback(
    (columnKey: string) => {
      clearFilter(columnKey);
      setCurrentPage(1);
    },
    [clearFilter],
  );

  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    setCurrentPage(1);
  }, [clearAllFilters]);

  const setEntryDateFrom = useCallback(
    (value: string) => {
      setDateFrom(value);
      setCurrentPage(1);
    },
    [setDateFrom],
  );

  const setEntryDateTo = useCallback(
    (value: string) => {
      setDateTo(value);
      setCurrentPage(1);
    },
    [setDateTo],
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, unknown>,
    sorter: SorterResult<Sample> | SorterResult<Sample>[],
  ) => {
    let nextSortField: string | undefined;
    let nextSortOrder: 'ascend' | 'descend' | undefined;

    if (Array.isArray(sorter)) {
      const activeSorter = sorter.find((item) => item.order);
      if (activeSorter) {
        nextSortField = (activeSorter.columnKey as string) || undefined;
        nextSortOrder =
          activeSorter.order === 'ascend' || activeSorter.order === 'descend'
            ? activeSorter.order
            : undefined;
      }
    } else if (sorter?.order) {
      nextSortField = (sorter.columnKey as string) || undefined;
      nextSortOrder =
        sorter.order === 'ascend' || sorter.order === 'descend'
          ? sorter.order
          : undefined;
    }

    setSortField(nextSortField);
    setSortOrder(nextSortOrder);

    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setCurrentPage(1);
      return;
    }
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  return {
    columnFilters,
    currentPage,
    getActiveFiltersCount,
    entryDateBounds,
    entryDateFrom,
    entryDateTo,
    filterOptionsData,
    handleClearAllFilters,
    handleFilterChange,
    handleFilterClear,
    handleSearch,
    handleTableChange,
    isSamplesLoading,
    pageSize,
    samples,
    searchInput,
    setEntryDateFrom,
    setEntryDateTo,
    setSearchInput,
    totalSamples,
  };
};
